import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  PlusIcon, FolderIcon, FunnelIcon,
  Squares2X2Icon, ListBulletIcon, CalendarIcon, UserGroupIcon,
  CurrencyDollarIcon, ChartBarIcon, ClockIcon, BuildingOfficeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, Button, SearchInput, Select, AvatarGroup,
  StatusBadge, PriorityBadge, ProgressBar, Pagination, EmptyState, PageLoader, Badge
} from '../../components/ui';
import { projectService, clientService } from '../../services';
import { useAuthStore } from '../../stores';

export default function ProjectList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClients, setLoadingClients] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    client: '',
    sort: '-createdAt'
  });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    onHold: 0,
    totalBudget: 0,
    avgProgress: 0
  });

  useEffect(() => {
    fetchProjects();
    fetchClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);

  useEffect(() => {
    calculateStatsWithPermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, currentUser]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort: filters.sort,
        ...filters
      };
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      const response = await projectService.getProjects(params);
      setProjects(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await clientService.getClients({ limit: 100 });
      setClients(response.data || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };


  const handleSearch = useCallback((value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      client: '',
      sort: '-createdAt'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const statusOptions = [
    { value: '', label: t('common.all') },
    { value: 'draft', label: t('projects.statuses.draft') },
    { value: 'active', label: t('projects.statuses.active') },
    { value: 'on_hold', label: t('projects.statuses.onHold') },
    { value: 'completed', label: t('projects.statuses.completed') },
    { value: 'cancelled', label: t('projects.statuses.cancelled') }
  ];

  const priorityOptions = [
    { value: '', label: t('common.all') },
    { value: 'low', label: t('projects.priorities.low') },
    { value: 'medium', label: t('projects.priorities.medium') },
    { value: 'high', label: t('projects.priorities.high') },
    { value: 'urgent', label: t('projects.priorities.urgent') }
  ];

  const sortOptions = [
    { value: '-createdAt', label: t('common.newest') || 'Newest First' },
    { value: 'createdAt', label: t('common.oldest') || 'Oldest First' },
    { value: 'name', label: t('common.name') + ' (A-Z)' },
    { value: '-name', label: t('common.name') + ' (Z-A)' },
    { value: '-progress', label: t('common.progress') + ' (High)' },
    { value: 'progress', label: t('common.progress') + ' (Low)' },
    { value: '-endDate', label: t('projects.endDate') + ' (Latest)' },
    { value: 'endDate', label: t('projects.endDate') + ' (Earliest)' }
  ];

  const clientOptions = [
    { value: '', label: t('common.all') },
    ...clients.map(client => ({
      value: client._id,
      label: client.name
    }))
  ];

  const hasActiveFilters = filters.status || filters.priority || filters.client;

  // Check if user can view financials for a specific project
  const canViewFinancials = (project) => {
    if (!currentUser || !project) return false;

    // Owner can always view financials
    if (project.owner?._id?.toString() === currentUser._id?.toString()) {
      return true;
    }

    // Check member permissions
    const member = project.members?.find(m =>
      m.user?._id?.toString() === currentUser._id?.toString() ||
      m.user?.toString() === currentUser._id?.toString()
    );

    return member?.permissions?.canViewFinancials === true;
  };

  // Calculate stats only for projects where user can view financials
  const calculateStatsWithPermissions = () => {
    if (projects.length === 0) {
      setStats({
        total: 0,
        active: 0,
        completed: 0,
        onHold: 0,
        totalBudget: 0,
        avgProgress: 0
      });
      return;
    }

    const active = projects.filter(p => p.status === 'active').length;
    const completed = projects.filter(p => p.status === 'completed').length;
    const onHold = projects.filter(p => p.status === 'on_hold').length;

    // Only include budget from projects where user has permission
    const totalBudget = projects.reduce((sum, p) => {
      if (canViewFinancials(p) && p.budget) {
        return sum + p.budget;
      }
      return sum;
    }, 0);

    const avgProgress = projects.length > 0
      ? Math.round(projects.reduce((sum, p) => sum + (p.progress || 0), 0) / projects.length)
      : 0;

    setStats({
      total: pagination.total,
      active,
      completed,
      onHold,
      totalBudget,
      avgProgress
    });
  };

  const statsCards = [
    {
      label: t('common.all'),
      value: stats.total,
      icon: FolderIcon,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50'
    },
    {
      label: t('projects.statuses.active'),
      value: stats.active,
      icon: ChartBarIcon,
      color: 'text-green-500',
      bgColor: 'bg-green-50'
    },
    {
      label: t('projects.statuses.completed'),
      value: stats.completed,
      icon: ChartBarIcon,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50'
    },
    {
      label: t('common.progress'),
      value: `${stats.avgProgress}%`,
      icon: ChartBarIcon,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
      subtitle: t('dashboard.avgProgress') || 'Average'
    },
    // Only show budget card if user has permission to view financials in at least one project
    ...(projects.some(p => canViewFinancials(p)) ? [{
      label: t('projects.budget'),
      value: `EGP ${(stats.totalBudget / 1000).toFixed(0)}K`,
      icon: CurrencyDollarIcon,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-50',
      subtitle: t('dashboard.totalBudget') || 'Total'
    }] : [])
  ];

  return (
    <>
      <Helmet>
        <title>{t('projects.title')} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={t('projects.title')}
        actions={
          <Button icon={PlusIcon} onClick={() => navigate('/projects/new')}>
            {t('projects.newProject')}
          </Button>
        }
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {statsCards.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                  <p className="text-xl font-bold text-neutral-dark">{stat.value}</p>
                  {stat.subtitle && (
                    <p className="text-xs text-gray-400 mt-1">{stat.subtitle}</p>
                  )}
                </div>
                <div className={`${stat.bgColor} p-2.5 rounded-lg`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col gap-4">
            {/* Main Search and View Toggle */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex-1 w-full">
                <SearchInput
                  value={filters.search}
                  onChange={handleSearch}
                  placeholder={t('common.search') + ' ' + t('projects.title').toLowerCase() + '...'}
                  className="w-full"
                />
              </div>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                    title="Grid View"
                  >
                    <Squares2X2Icon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                    title="List View"
                  >
                    <ListBulletIcon className="w-4 h-4" />
                  </button>
                </div>
                {/* Filter Toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  icon={FunnelIcon}
                  onClick={() => setShowFilters(!showFilters)}
                  className={hasActiveFilters ? 'border-primary text-primary' : ''}
                >
                  {t('common.filter')}
                  {hasActiveFilters && (
                    <span className="ml-1 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                      {[filters.status, filters.priority, filters.client].filter(Boolean).length}
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Select
                    label={t('common.status')}
                    options={statusOptions}
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  />
                  <Select
                    label={t('common.priority')}
                    options={priorityOptions}
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                  />
                  <Select
                    label={t('projects.client')}
                    options={clientOptions}
                    value={filters.client}
                    onChange={(e) => handleFilterChange('client', e.target.value)}
                    disabled={loadingClients}
                  />
                  <Select
                    label={t('common.sort') || 'Sort By'}
                    options={sortOptions}
                    value={filters.sort}
                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                  />
                </div>
                {hasActiveFilters && (
                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      icon={XMarkIcon}
                    >
                      {t('common.clearFilters') || 'Clear Filters'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Projects List */}
      {loading ? (
        <PageLoader />
      ) : projects.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={FolderIcon}
              title={t('projects.noProjects')}
              description={t('projects.noProjectsDesc')}
              action={() => navigate('/projects/new')}
              actionLabel={t('projects.newProject')}
            />
          </CardBody>
        </Card>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((project) => {
                // Combine owner and members, removing duplicates (owner might be in members array)
                const ownerId = project.owner?._id?.toString();
                const uniqueMembers = project.members
                  .map(m => m.user)
                  .filter(member => member && member._id?.toString() !== ownerId);
                const allMembers = [project.owner, ...uniqueMembers].filter(Boolean);
                const daysRemaining = project.endDate ? dayjs(project.endDate).diff(dayjs(), 'day') : null;
                const isOverdue = daysRemaining !== null && daysRemaining < 0 && project.status !== 'completed';

                return (
                  <Card key={project._id} className="hover:shadow-lg transition-all cursor-pointer group">
                    <Link to={`/projects/${project._id}`}>
                      <CardBody>
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-lg text-neutral-dark line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                              {project.name}
                            </h3>
                            {project.client && (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <BuildingOfficeIcon className="w-4 h-4" />
                                <Link
                                  to={`/clients/${project.client._id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="hover:text-primary hover:underline"
                                >
                                  {project.client.name}
                                </Link>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <StatusBadge status={project.status} />
                            <PriorityBadge priority={project.priority} />
                          </div>
                        </div>

                        {/* Description */}
                        {project.description && (
                          <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                            {project.description}
                          </p>
                        )}

                        {/* Progress */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-500">{t('common.progress')}</span>
                            <span className="font-semibold text-primary">{project.progress}%</span>
                          </div>
                          <ProgressBar value={project.progress} color="primary" size="md" />
                        </div>

                        {/* Info Grid */}
                        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                          {project.startDate && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <CalendarIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-xs">
                                {t('projects.started')}: {dayjs(project.startDate).format('MMM D, YYYY')}
                              </span>
                            </div>
                          )}
                          {project.endDate && (
                            <div className={`flex items-center gap-2 ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
                              <ClockIcon className="w-4 h-4" />
                              <span className="text-xs">
                                {t('projects.endDate')}: {dayjs(project.endDate).format('MMM D, YYYY')}
                                {isOverdue && ' ⚠️'}
                              </span>
                            </div>
                          )}
                          {canViewFinancials(project) && project.budget > 0 && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-xs">
                                {project.currency || 'EGP'} {(project.budget / 1000).toFixed(0)}K
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-gray-600">
                            <UserGroupIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-xs">
                              {allMembers.length} {allMembers.length === 1 ? t('projects.member') : t('projects.members')}
                            </span>
                          </div>
                        </div>

                        {/* Tags */}
                        {project.tags && project.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {project.tags.slice(0, 3).map((tag, index) => (
                              <Badge key={index} variant="neutral" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {project.tags.length > 3 && (
                              <Badge variant="neutral" className="text-xs">
                                +{project.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <AvatarGroup
                            users={allMembers.slice(0, 4)}
                            max={4}
                            size="xs"
                          />
                          {project.category && (
                            <Badge variant="secondary" className="text-xs">
                              {project.category}
                            </Badge>
                          )}
                        </div>
                      </CardBody>
                    </Link>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardBody>
                <div className="space-y-3">
                  {projects.map((project) => {
                    // Combine owner and members, removing duplicates (owner might be in members array)
                    const ownerId = project.owner?._id?.toString();
                    const uniqueMembers = project.members
                      .map(m => m.user)
                      .filter(member => member && member._id?.toString() !== ownerId);
                    const allMembers = [project.owner, ...uniqueMembers].filter(Boolean);
                    const daysRemaining = project.endDate ? dayjs(project.endDate).diff(dayjs(), 'day') : null;
                    const isOverdue = daysRemaining !== null && daysRemaining < 0 && project.status !== 'completed';

                    return (
                      <Link
                        key={project._id}
                        to={`/projects/${project._id}`}
                        className="block p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all"
                      >
                        <div className="flex items-center gap-4">
                          {/* Status Indicator */}
                          <div className={`w-1 h-16 rounded-full ${project.status === 'completed' ? 'bg-green-500' :
                            project.status === 'active' ? 'bg-blue-500' :
                              project.status === 'on_hold' ? 'bg-yellow-500' :
                                project.status === 'cancelled' ? 'bg-red-500' :
                                  'bg-gray-400'
                            }`} />

                          {/* Project Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg text-neutral-dark">
                                {project.name}
                              </h3>
                              <StatusBadge status={project.status} />
                              <PriorityBadge priority={project.priority} />
                              {project.category && (
                                <Badge variant="secondary">{project.category}</Badge>
                              )}
                            </div>
                            {project.description && (
                              <p className="text-sm text-gray-600 line-clamp-1 mb-2">
                                {project.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              {project.client && (
                                <div className="flex items-center gap-1">
                                  <BuildingOfficeIcon className="w-4 h-4" />
                                  <Link
                                    to={`/clients/${project.client._id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="hover:text-primary hover:underline"
                                  >
                                    {project.client.name}
                                  </Link>
                                </div>
                              )}
                              {project.startDate && (
                                <div className="flex items-center gap-1">
                                  <CalendarIcon className="w-4 h-4" />
                                  <span>{dayjs(project.startDate).format('MMM D, YYYY')}</span>
                                </div>
                              )}
                              {project.endDate && (
                                <div className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                                  <ClockIcon className="w-4 h-4" />
                                  <span>
                                    {dayjs(project.endDate).format('MMM D, YYYY')}
                                    {isOverdue && ' (Overdue)'}
                                  </span>
                                </div>
                              )}
                              {canViewFinancials(project) && project.budget > 0 && (
                                <div className="flex items-center gap-1">
                                  <CurrencyDollarIcon className="w-4 h-4" />
                                  <span>{project.currency || 'EGP'} {(project.budget / 1000).toFixed(0)}K</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Progress and Team */}
                          <div className="flex items-center gap-6">
                            <div className="text-center min-w-[80px]">
                              <p className="text-xs text-gray-500 mb-1">{t('common.progress')}</p>
                              <p className="text-lg font-bold text-primary">{project.progress}%</p>
                              <ProgressBar value={project.progress} size="sm" className="mt-1" />
                            </div>
                            <div className="text-center min-w-[100px]">
                              <p className="text-xs text-gray-500 mb-1">{t('projects.team')}</p>
                              <AvatarGroup
                                users={allMembers.slice(0, 4)}
                                max={4}
                                size="xs"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                {allMembers.length} {allMembers.length === 1 ? t('projects.member') : t('projects.members')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={pagination.page}
                limit={pagination.limit}
                total={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}